import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Bell } from 'lucide-react';

import {
  useNotifications,
  useInvitations,
  useAppShell,
  useHeroScrolled,
  usePermissions,
} from '@/hooks';
import { groupByTime } from '@/services/notification.helpers';
import { ACTIVITY_WINDOW_DAYS } from '@/constants/activity';
import type {
  Child,
  HouseholdMemberWithUser,
  Notification,
  NotificationFilter,
} from '@/types';
import { FilterChipGroup, Menu, MenuItem, Button } from '@/components/ui';
import { toast, useUserStore } from '@/stores';
import { NotificationItem } from '@/components/domain/shared/NotificationItem';
import { TaskEventDetailModal } from '@/components/domain/shared/TaskEventDetailModal';
import { ItemFormModal, type ItemFormData } from '@/pages/review/ItemFormModal';
import { EmptyState } from '@/components/states';
import { HamburgerMenuButton } from '@/components/Layout/mobile/HamburgerMenuButton';
import { scheduleService } from '@/services/schedule.service';
import { buildSeriesDelete } from '@/lib/seriesDelete';
import { handleFailedEmailTap } from '@/services/email.service';
import { getChildren, getMembers } from '@/services/household.service';
import { getUserRole } from '@/services/user.service';
import { config } from '@/lib/config';
import { toIsoWithTz } from '@/lib/tz';
import {
  getMembersFromTask,
  getMembersFromEvent,
  taskToReviewItem,
  eventToReviewItem,
  type TaskRow,
  type EventRow,
} from '@/lib/schedule';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
];

/** Task/event opened over the feed — detail sheet and edit form share it. */
type ActivityItem =
  | { kind: 'task'; data: TaskRow }
  | { kind: 'event'; data: EventRow };

export function Activity() {
  const navigate = useNavigate();
  const context = useAppShell();
  const [heroRef, isScrolled] = useHeroScrolled();

  const {
    items,
    filter,
    hasMore,
    loading,
    load,
    changeFilter,
    tapItem,
    markAllAsRead,
    householdId,
  } = useNotifications();

  const { invitationNotifications, acceptInvitation, declineInvitation } =
    useInvitations();

  const userTimezone = useUserStore((s) => s.timezone);
  const { canEdit, canComplete, canDelete, isOwner } = usePermissions();

  // YAR-386: the overflow menu had NO role gate, so every level saw
  // "Notification settings" and it navigated. Notification settings is a
  // PRIMARY-only surface — the page's only content is the SMS/evening-digest
  // block, itself gated on `config.enableSms && isPrimary`, because the
  // digest job only ever sends to the household PRIMARY. So Settings hides
  // its row while Activity did not, and Can view/Can edit landed on an empty
  // page. YAR-170 AC6: hidden, not disabled, leaving a one-item menu.
  //
  // This mirrors the destination's own render condition rather than naming
  // roles, so when the post-onboarding opt-in widens who has notification
  // settings, this gate widens with it (YAR-170's open question).
  const canManageNotifications = config.enableSms && isOwner;

  // Modal state for task/event detail overlays, plus the edit form the
  // detail sheet's pencil opens (YAR-383).
  const [modal, setModal] = useState<ActivityItem | null>(null);
  const [editingItem, setEditingItem] = useState<ActivityItem | null>(null);
  // A task's related event, resolved when its detail sheet opens: powers the
  // category chip and keeps ItemFormModal's "Related Event" select honest.
  // Activity has no event list of its own to draw those options from.
  const [relatedEvent, setRelatedEvent] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [householdChildren, setHouseholdChildren] = useState<Child[]>([]);
  const [householdMembers, setHouseholdMembers] = useState<
    HouseholdMemberWithUser[]
  >([]);

  // Load on mount, and re-load whenever the active household changes — a
  // switch must replace the feed, not leave the previous household's on
  // screen (YAR-358).
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load is stable (uses refs)
  }, [householdId]);

  // Reload when filter changes
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load is stable (uses refs)
  }, [filter]);

  // Household roster for the edit form. It has to be here rather than
  // fetched when the pencil is tapped: ItemFormModal resolves
  // `initialData.child_names` against `availableChildren` inside a useState
  // INITIALIZER, so a roster that arrives after the form mounts leaves the
  // selection empty and the save silently clears every child assignment.
  // Skipped entirely for Can view, which never opens the form.
  useEffect(() => {
    if (!householdId || !canEdit) return;
    let cancelled = false;
    void (async () => {
      try {
        const [kids, members] = await Promise.all([
          getChildren(householdId),
          getMembers(householdId),
        ]);
        if (!cancelled) {
          setHouseholdChildren(kids);
          setHouseholdMembers(members);
        }
      } catch (err) {
        // Non-fatal: the feed itself doesn't need the roster.
        console.error('[Activity] failed to load children/members:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, canEdit]);

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          load();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, load]);

  const handleFilterChange = useCallback(
    (value: string) => {
      changeFilter(value as NotificationFilter);
    },
    [changeFilter],
  );

  const handleTap = useCallback(
    async (notification: Notification) => {
      await tapItem(notification.id);

      // YAR-171 AC5: a "couldn't be read" row retries IN PLACE on the first
      // failure — no navigation; the email status toast narrates the re-read.
      // After a second failure it opens Review, where the card carries
      // Discard and Try again. Decided against the email's LIVE state: the
      // row's copy was frozen when it was written.
      if (notification.type === 'email_processing_failed') {
        const emailId = notification.metadata?.email_id;
        if (typeof emailId === 'string' && householdId) {
          try {
            const outcome = await handleFailedEmailTap(emailId, householdId);
            if (outcome !== 'open-review') return;
          } catch {
            // Could not read or retry it from here — Review is where the
            // parent can act on it either way.
          }
        }
        navigate('/review');
        return;
      }

      const deepLink = notification.deep_link;
      if (!deepLink) return;

      // Category 1: Modal — stay on Activity
      if (deepLink.startsWith('task/detail/')) {
        try {
          // Supabase TASK_SELECT row matches TaskRow structurally; the cast
          // avoids importing the generated DB types here (same as TodayTab).
          const data = await scheduleService.getTaskById(
            deepLink.split('/')[2],
          );
          if (!data) return;
          const task = data as unknown as TaskRow;
          setModal({ kind: 'task', data: task });
          setRelatedEvent(null);
          if (task.related_event_id) {
            try {
              const ev = await scheduleService.getEventById(
                task.related_event_id,
              );
              if (ev) {
                const event = ev as unknown as EventRow;
                setRelatedEvent({ id: event.id, title: event.title });
              }
            } catch {
              // Best-effort: without it the chip and the "Related Event"
              // option are simply absent, which is what shipped before.
            }
          }
        } catch {
          toast.error('Could not load details. Please try again.');
        }
      } else if (deepLink.startsWith('event/detail/')) {
        try {
          const data = await scheduleService.getEventById(
            deepLink.split('/')[2],
          );
          if (data) {
            setRelatedEvent(null);
            setModal({ kind: 'event', data: data as unknown as EventRow });
          }
        } catch {
          toast.error('Could not load details. Please try again.');
        }
      }
      // Category 2: Settings subpage — back returns to Activity
      else if (deepLink.startsWith('settings/')) {
        navigate(`/${deepLink}`, { state: { from: '/activity' } });
      }
      // Category 3: Main nav — no back to Activity
      else {
        navigate(`/${deepLink}`);
      }
    },
    [tapItem, navigate, householdId],
  );

  const handleMarkAllRead = useCallback(async () => {
    await markAllAsRead();
  }, [markAllAsRead]);

  const handleManageNotifications = useCallback(() => {
    navigate('/settings/notifications', { state: { from: '/activity' } });
  }, [navigate]);

  // ── Task/event mutations from the detail sheet (YAR-383) ──────────
  // Gated client-side on usePermissions, which mirrors the RLS grants:
  // UPDATE on tasks/events is my_writable_household_ids() (Admin +
  // Co-Admin + Editor) and DELETE is my_manager_household_ids()
  // (Admin + Co-Admin) — see 20260709120300_rls_per_command.sql.

  const handleToggleTask = useCallback(
    async (taskId: string, currentStatus: string) => {
      try {
        await scheduleService.toggleTaskStatus(taskId, currentStatus);
      } catch (err) {
        console.error('[Activity] failed to toggle task:', err);
        toast.error('Could not update the task. Please try again.');
      }
    },
    [],
  );

  const handleEditItem = useCallback(
    async (data: ItemFormData) => {
      if (!editingItem) return;
      const childIds = data.child_names
        .map((name) => householdChildren.find((c) => c.name === name)?.id)
        .filter((id): id is string => typeof id === 'string');
      const toIso = (dt: string) => toIsoWithTz(dt, userTimezone);

      if (editingItem.kind === 'task') {
        const raw = data.due_datetime ?? data.start_datetime ?? null;
        await scheduleService.updateTask(editingItem.data.id, {
          title: data.title,
          due_date: raw ? toIso(raw) : null,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          notes: data.description ?? null,
          location: data.location ?? null,
          links: data.links,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
          // YAR-309: the form has emitted this since YAR-295 wired up the
          // "Related Event" dropdown; every handleEditItem simply never
          // forwarded it, so clearing or changing the link was a no-op.
          related_event_id: data.related_event_id,
        });
      } else {
        await scheduleService.updateEvent(editingItem.data.id, {
          title: data.title,
          start_date: data.start_datetime
            ? toIso(data.start_datetime)
            : editingItem.data.start_date,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          location: data.location ?? null,
          links: data.links,
          notes: data.description ?? null,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
        });
      }

      setEditingItem(null);
      // Editing fires its own activity entries server-side, so pull the feed
      // fresh rather than leaving the user staring at the pre-edit list.
      await load(true);
    },
    [editingItem, householdChildren, load, userTimezone],
  );

  // No auto-mark-read on leaving (YAR-496 behaviour 1, YAR-168 AC17): an
  // item turns read only when the parent taps it or uses `Mark all read`.
  // Opening, scrolling or leaving Activity never marks anything read — the
  // old "Spec 090" unmount cleanup did, so unread items vanished unseen.

  // Drives the drawer dot and whether `Mark all read` is enabled — never
  // whether the overflow menu renders (see OverflowMenu). Deliberately
  // derived from the loaded rows rather than the server-side count: YAR-170
  // AC1 and YAR-169 AC3 define the disabled state as "the Unread filter is
  // empty / every item is read", which is a statement about what is on
  // screen. Only its zero-ness is read here, so it is unaffected by the
  // duplicate-page defect in YAR-388 (which could inflate the number but
  // never turn a non-zero count into zero).
  const unreadCount = items.filter((n) => !n.read_at).length;
  const grouped = groupByTime(items);
  const isEmpty = !loading && items.length === 0;

  return (
    <div className="flex flex-col min-h-full bg-surface">
      {/* Fixed compressed header — appears on scroll */}
      <div
        className={[
          'fixed top-0 left-0 right-0 z-[100] bg-background transition-all duration-200',
          isScrolled
            ? 'opacity-100 translate-y-0 border-b border-border'
            : 'opacity-0 -translate-y-full pointer-events-none',
        ].join(' ')}
      >
        <div className="flex items-center justify-between px-5 py-2">
          <div className="flex-1 min-w-0">
            {context?.onOpenDrawer && (
              <HamburgerMenuButton
                onClick={context.onOpenDrawer}
                showBadge={unreadCount > 0}
                variant="compact"
              />
            )}
          </div>
          <span className="text-base font-semibold text-foreground">
            Activity
          </span>
          <div className="flex-1 min-w-0 flex justify-end">
            <OverflowMenu
              markAllReadDisabled={unreadCount === 0}
              onMarkAllRead={handleMarkAllRead}
              onManageNotifications={
                canManageNotifications ? handleManageNotifications : undefined
              }
            />
          </div>
        </div>
        <div className="px-5 pb-3">
          <FilterChipGroup
            options={FILTER_OPTIONS}
            value={filter}
            onChange={handleFilterChange}
          />
        </div>
      </div>

      {/* Hero header — hides on scroll */}
      <div
        ref={heroRef as React.RefObject<HTMLDivElement>}
        className="p-5"
      >
        {context?.onOpenDrawer && (
          <HamburgerMenuButton
            onClick={context.onOpenDrawer}
            showBadge={unreadCount > 0}
          />
        )}
        <div className="flex items-center justify-between mt-4">
          <h1 className="text-[32px] font-bold leading-10 text-foreground">
            Activity
          </h1>
          <OverflowMenu
            markAllReadDisabled={unreadCount === 0}
            onMarkAllRead={handleMarkAllRead}
            onManageNotifications={
              canManageNotifications ? handleManageNotifications : undefined
            }
          />
        </div>

        <div className="mt-4">
          <FilterChipGroup
            options={FILTER_OPTIONS}
            value={filter}
            onChange={handleFilterChange}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 pb-24">
        {/* Loading skeleton */}
        {loading && items.length === 0 && (
          <div className="flex flex-col gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="px-5 py-3 animate-pulse"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted" />
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                  <div className="h-3 bg-muted rounded w-12" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty states — copy per YAR-169 AC4 (Unread) and YAR-168 AC10
            (All); QA flagged the old "notifications" wording on YAR-316/321.
            The surface is "activity", never "notification" (YAR-168 AC15). */}
        {isEmpty && filter === 'unread' && (
          <EmptyState
            icon={<Bell size={40} />}
            title="You're all caught up"
            description="No unread activity"
          />
        )}

        {isEmpty && filter === 'all' && (
          <EmptyState
            icon={<Bell size={40} />}
            title="No activity yet"
            description="Activity will appear here from today onward"
          />
        )}

        {/* Invitation notifications (always at top) */}
        {invitationNotifications.length > 0 && (
          <div>
            {invitationNotifications.map((invitation) => (
              <div
                key={invitation.id}
                className="flex items-start gap-3 px-5 py-3 bg-muted"
              >
                <div className="pt-0.5 shrink-0">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-primary-light text-primary">
                    <Bell size={16} />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    {invitation.title}
                  </p>
                  {invitation.body && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {invitation.body}
                    </p>
                  )}
                  {typeof invitation.metadata?.invitation_id === 'string' && (
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={async () => {
                          const id = invitation.metadata!
                            .invitation_id as string;
                          const label =
                            (invitation.metadata?.household_name as string) ??
                            'the household';
                          try {
                            await acceptInvitation(id);
                            // Accepting seats a brand-new membership, but the
                            // caller's role (userStore) and active household
                            // (AppShell) were bootstrapped at sign-in and won't
                            // reflect it until a reload — so Today/Schedule
                            // render empty and Review/FAB stay hidden. Re-run
                            // the SAME YAR-269 resolution both use so the app
                            // reflects the join immediately (best-effort: a
                            // failure here just defers it to the next reload).
                            try {
                              if (context.refreshHousehold) {
                                // Re-resolves the household AND the level in
                                // it in ONE pass, writing both together.
                                // Calling getUserRole() as well used to
                                // repeat the whole waterfall — and, worse,
                                // resolved the role SEPARATELY, so the two
                                // could disagree about which household the
                                // level belonged to.
                                await context.refreshHousehold();
                              } else {
                                // No shell context (shouldn't happen here) —
                                // fall back so the role still updates.
                                useUserStore
                                  .getState()
                                  .setRole(await getUserRole());
                              }
                            } catch {
                              /* non-blocking */
                            }
                            toast.success(`Joined ${label}`);
                          } catch (err) {
                            console.error(
                              '[Activity] accept invitation failed:',
                              err,
                            );
                            toast.error(
                              err instanceof Error
                                ? err.message
                                : 'Could not accept the invitation — please try again.',
                            );
                          }
                        }}
                      >
                        Accept
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={async () => {
                          const id = invitation.metadata!
                            .invitation_id as string;
                          try {
                            await declineInvitation(id);
                            toast.success('Invitation declined');
                          } catch (err) {
                            console.error(
                              '[Activity] decline invitation failed:',
                              err,
                            );
                            toast.error(
                              err instanceof Error
                                ? err.message
                                : 'Could not decline the invitation — please try again.',
                            );
                          }
                        }}
                      >
                        Decline
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grouped notification list */}
        {grouped.map(({ group, items: groupItems }) => (
          <div key={group}>
            <div className="px-5 pt-4 pb-2">
              <h2 className="text-sm font-semibold text-foreground">{group}</h2>
            </div>
            {groupItems.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onTap={handleTap}
              />
            ))}
          </div>
        ))}

        {/* Retention caption — YAR-168 AC16 (C21), YAR-387. One list-end
            line once the last page is in, on All and Unread alike; never in
            an empty state (AC10), which returns before any content. The
            window it promises is enforced by the service's display boundary. */}
        {!hasMore && items.length > 0 && (
          <p className="px-5 pt-5 pb-4 text-center text-sm text-muted-foreground">
            Showing activity from the last {ACTIVITY_WINDOW_DAYS} days
          </p>
        )}

        {/* Infinite scroll sentinel */}
        {hasMore && (
          <div
            ref={sentinelRef}
            className="h-10"
          />
        )}

        {/* Loading more indicator */}
        {loading && items.length > 0 && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}
      </div>

      {/* Task/Event detail from a notification deep-link. YAR-383: canEdit
          was hardcoded `false` and onToggleComplete was never passed, so this
          sheet was read-only for EVERY level — Admin and Co-Admin included,
          not just Can edit as filed. Both now come from usePermissions, the
          same source Today/Schedule/History use. */}
      {modal && (
        <TaskEventDetailModal
          item={modal}
          canEdit={canEdit}
          timezone={userTimezone}
          relatedEventTitle={
            modal.kind === 'task' ? relatedEvent?.title : undefined
          }
          onClose={() => setModal(null)}
          onEdit={() => {
            setEditingItem(modal);
            setModal(null);
          }}
          onToggleComplete={
            canComplete
              ? (id, status) => handleToggleTask(id, status)
              : undefined
          }
        />
      )}

      {editingItem && (
        <ItemFormModal
          mode="edit"
          initialType={editingItem.kind}
          initialData={
            editingItem.kind === 'task'
              ? taskToReviewItem(editingItem.data)
              : eventToReviewItem(editingItem.data)
          }
          // Activity holds a single deep-linked row, not a feed of events, so
          // the only option we can offer is the task's own related event —
          // enough for the select to render its current value as a name
          // instead of falling back to "None".
          availableEvents={relatedEvent ? [relatedEvent] : []}
          availableChildren={householdChildren.map((c) => ({
            name: c.name,
            emoji_avatar: c.emoji_avatar,
            emoji_color: c.emoji_color,
            avatar_image_path: c.avatar_image_path,
          }))}
          availableMembers={householdMembers
            .filter((m) => m.users)
            .map((m) => ({
              user_id: m.user_id,
              display_name: m.users!.display_name ?? m.users!.email ?? '',
              email: m.users!.email,
              avatar_emoji: m.users!.avatar_emoji,
              avatar_color: m.users!.avatar_color,
              avatar_image_path: m.users!.avatar_image_path,
            }))}
          initialMemberIds={
            editingItem.kind === 'task'
              ? getMembersFromTask(editingItem.data).map((m) => m.id)
              : getMembersFromEvent(editingItem.data).map((m) => m.id)
          }
          onSubmit={handleEditItem}
          onBack={() => setEditingItem(null)}
          seriesDelete={buildSeriesDelete({
            event: editingItem.kind === 'event' ? editingItem.data : null,
            householdId,
            canDelete,
            onDeleted: async () => {
              setEditingItem(null);
              await load(true);
            },
          })}
          onDelete={
            canDelete
              ? async () => {
                  if (editingItem.kind === 'task') {
                    await scheduleService.deleteTask(editingItem.data.id);
                  } else {
                    await scheduleService.deleteEvent(editingItem.data.id);
                  }
                  setEditingItem(null);
                  await load(true);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}

// ── Overflow Menu ─────────────────────────────────────────────────

/**
 * The trigger renders unconditionally — YAR-170 AC1 and YAR-169 AC3, both
 * settled on Jul 27: "the three-dot overflow icon is always visible", and at
 * zero unread `Mark all read` disables instead of the whole menu vanishing.
 * The April behaviour (`unreadCount > 0 &&` around this component) hid the
 * icon at zero unread, which took `Notification settings` — an item with
 * nothing to do with unread state — down with it, so the Admin lost the menu
 * entirely once they were caught up. That is what QA re-opened YAR-386 on.
 *
 * Disabled rather than absent because the two ACs say so and give the reason:
 * a control that disappears cannot distinguish "nothing to mark" from "this
 * feature is gone". The same rationale does NOT extend to the access gate
 * below — an item that is unavailable *forever at this level* is hidden,
 * while one that is unavailable *right now* is disabled.
 */
function OverflowMenu({
  onMarkAllRead,
  markAllReadDisabled = false,
  onManageNotifications,
}: {
  onMarkAllRead: () => void;
  /** True when nothing is unread: the row greys out, the menu still opens. */
  markAllReadDisabled?: boolean;
  /**
   * Omitted for levels that have no notification settings. YAR-170 AC6 is
   * explicit that the row is HIDDEN rather than disabled — "a disabled row
   * invites a tap and then explains nothing" — so the menu has to render as
   * a one-item menu, which it does.
   */
  onManageNotifications?: () => void;
}) {
  return (
    <Menu
      trigger={
        <button
          type="button"
          className="p-1 text-foreground hover:bg-muted rounded-md transition-colors"
          aria-label="Activity options"
        >
          <MoreVertical size={20} />
        </button>
      }
      align="right"
    >
      <MenuItem
        onClick={onMarkAllRead}
        disabled={markAllReadDisabled}
      >
        Mark all read
      </MenuItem>
      {onManageNotifications && (
        <MenuItem onClick={onManageNotifications}>
          Notification settings
        </MenuItem>
      )}
    </Menu>
  );
}
